// src/components/SemaphoreIndicator.jsx
// Indicador visual del semáforo para ofertas ciegas

import React from 'react';
import { Circle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Configuración de colores por estado (sin textos hardcodeados)
 */
const SEMAPHORE_CONFIG = {
  green: {
    color: 'text-green-400',
    bgColor: 'bg-green-400/20',
    borderColor: 'border-green-400/30',
    labelKey: 'semaphore.green.label',
    descKey: 'semaphore.green.desc'
  },
  yellow: {
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/20',
    borderColor: 'border-yellow-400/30',
    labelKey: 'semaphore.yellow.label',
    descKey: 'semaphore.yellow.desc'
  },
  red: {
    color: 'text-red-400',
    bgColor: 'bg-red-400/20',
    borderColor: 'border-red-400/30',
    labelKey: 'semaphore.red.label',
    descKey: 'semaphore.red.desc'
  }
};

/**
 * Indicador de semáforo (solo icono)
 */
export const SemaphoreIcon = ({ status, size = 'md' }) => {
  const config = SEMAPHORE_CONFIG[status] || SEMAPHORE_CONFIG.yellow;

  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  return (
    <Circle
      className={`${sizeClasses[size]} ${config.color} fill-current`}
      strokeWidth={0}
    />
  );
};

/**
 * Badge de semáforo con etiqueta
 */
export const SemaphoreBadge = ({ status, showLabel = true }) => {
  const { t } = useTranslation();
  const config = SEMAPHORE_CONFIG[status] || SEMAPHORE_CONFIG.yellow;

  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full ${config.bgColor} border ${config.borderColor}`}>
      <SemaphoreIcon status={status} size="sm" />
      {showLabel && (
        <span className={`text-xs font-medium ${config.color}`}>
          {t(config.labelKey)}
        </span>
      )}
      <Info className={`w-3 h-3 ${config.color}`} />
    </div>
  );
};

/**
 * Mensaje completo de semáforo con descripción
 */
export const SemaphoreMessage = ({ status }) => {
  const { t } = useTranslation();
  const config = SEMAPHORE_CONFIG[status] || SEMAPHORE_CONFIG.yellow;

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg ${config.bgColor} border ${config.borderColor}`}>
      <SemaphoreIcon status={status} size="lg" />
      <div>
        <p className={`text-sm font-medium ${config.color}`}>
          {t(config.labelKey)}
        </p>
        <p className="text-xs text-slate-400 mt-0.5">
          {t(config.descKey)}
        </p>
      </div>
    </div>
  );
};

/**
 * Componente principal (wrapper)
 */
function SemaphoreIndicator({ status, variant = 'icon', ...props }) {
  switch (variant) {
    case 'badge':
      return <SemaphoreBadge status={status} {...props} />;
    case 'message':
      return <SemaphoreMessage status={status} {...props} />;
    default:
      return <SemaphoreIcon status={status} {...props} />;
  }
}

export default SemaphoreIndicator;
